import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { observer } from 'mobx-react-lite'
import InitialAvatar from 'shared/components/InitialAvatar/InitialAvatar'
import { ReactComponent as SearchIcon } from 'assets/icons/search_empty.svg'
import { ReactComponent as ClockIcon } from 'assets/icons/clock.svg'
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg'
import { ensureStudentsLoaded } from 'pages/Students/model/ensureStudentsLoaded'
import { studentsStore } from 'pages/Students/model/studentsStore'
import { noteTakerStore } from 'shared/stores/noteTakerStore'
import s from './AssignRecordingStudentModal.module.scss'

const MODAL_COPY = {
	change: {
		kicker: 'Recording in progress',
		title: 'Select student',
		subtitle: 'Choose who this session note is for.',
	},
	finish: {
		kicker: 'Recording captured',
		title: 'Who is this note for?',
		subtitle:
			'Koda could not reliably detect the student. Select one student to attach this session note.',
	},
}

const AssignRecordingStudentModal = observer(() => {
	const [search, setSearch] = useState('')
	const [selectedId, setSelectedId] = useState('')
	const isOpen = noteTakerStore.isAssignStudentOpen
	const modalMode = noteTakerStore.assignStudentModalMode || 'finish'
	const copy = MODAL_COPY[modalMode] || MODAL_COPY.finish

	useEffect(() => {
		if (!isOpen) return
		ensureStudentsLoaded().catch(error => {
			console.error('Failed to load students for note assignment:', error)
		})
	}, [isOpen])

	useEffect(() => {
		if (!isOpen) {
			setSearch('')
			setSelectedId('')
			return
		}

		const currentId = noteTakerStore.assignedStudentId
		if (modalMode === 'change' && currentId) {
			setSelectedId(String(currentId))
		}
	}, [isOpen, modalMode])

	if (!isOpen) return null

	const list = studentsStore.students || []
	const query = search.trim().toLowerCase()
	const filteredStudents = !query
		? list.slice(0, 6)
		: list
				.filter(student => {
					const name = String(student?.name || '').toLowerCase()
					const grade = String(student?.grade || '')
					const section = String(student?.section || '').toLowerCase()
					return (
						name.includes(query) ||
						grade.includes(query) ||
						section.includes(query)
					)
				})
				.slice(0, 8)

	const saveSelection = () => {
		if (!selectedId) return
		noteTakerStore.confirmStudentAssignment(selectedId)
	}

	return createPortal(
		<div className={s.overlay} role="dialog" aria-modal="true">
			<button
				type="button"
				className={s.scrim}
				aria-label="Close student picker"
				onClick={() => noteTakerStore.closeStudentAssignment()}
			/>
			<section className={s.card}>
				<button
					type="button"
					className={s.closeButton}
					aria-label="Close"
					onClick={() => noteTakerStore.closeStudentAssignment()}
				>
					<CrossIcon />
				</button>
				<div className={s.header}>
					<div className={s.kicker}>{copy.kicker}</div>
					<h2 className={s.title}>{copy.title}</h2>
					<p className={s.subtitle}>{copy.subtitle}</p>
				</div>

				<label className={s.search}>
					<SearchIcon />
					<input
						type="text"
						placeholder="Search students"
						value={search}
						onChange={event => setSearch(event.target.value)}
					/>
				</label>

				<div className={s.listHead}>
					<ClockIcon />
					<span>Recent students</span>
				</div>
				<div className={s.list}>
					{filteredStudents.map(student => {
						const isActive = String(selectedId) === String(student.id)
						return (
							<button
								key={student.id}
								type="button"
								className={[s.row, isActive ? s.rowActive : '']
									.filter(Boolean)
									.join(' ')}
								onClick={() => setSelectedId(String(student.id))}
							>
								<InitialAvatar
									name={student.name}
									size={40}
									avatarKey={student.avatarKey}
									avatarSource={student.avatarSource}
									photoUrl={student.photoUrl}
								/>
								<span className={s.rowText}>
									<strong>{student.name || 'Student'}</strong>
									<small>
										{student.grade ? `Grade ${student.grade}` : 'Student'}
										{student.section ? ` - Section ${student.section}` : ''}
									</small>
								</span>
								<span
									className={[s.radio, isActive ? s.radioActive : '']
										.filter(Boolean)
										.join(' ')}
								>
									<span className={s.radioDot} />
								</span>
							</button>
						)
					})}

					{filteredStudents.length === 0 ? (
						<div className={s.empty}>No matching students found.</div>
					) : null}
				</div>

				<footer className={s.footer}>
					<button
						type="button"
						className={s.primaryButton}
						disabled={!selectedId}
						onClick={saveSelection}
					>
						Save
					</button>
				</footer>
			</section>
		</div>,
		document.body,
	)
})

export default AssignRecordingStudentModal
